import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import './_BaseOnOff.scss';
import omit from 'lodash/omit';
import noop from 'lodash/noop';

/**
 * base component button for indicating on/off status
 */
export default class BaseOnOff extends Component {
  /**
   * constructor
   * @param props
   */
  constructor(props) {
    super(props);

    this.state = {
      checked: !!('checked' in props && typeof props.checked === 'boolean'
        ? props.checked
        : props.defaultChecked),
      focused: false
    };
  }

  /**
   * componentWillReceieveProps
   * @param nextProps
   */
  UNSAFE_componentWillReceiveProps(nextProps) {
    if ('checked' in nextProps) {
      this.setState({ checked: !!nextProps.checked });
    }
  }

  /*
   * setChecked - set the checked state.
   * Updating the state only if this.state.checked is uncontrolled, meaning this.props.checked doesn't exist
   */
  setChecked(checked) {
    if (!('checked' in this.props)) {
      this.setState({ checked: !!checked });
    }

    if (this.props.onChange) {
      this.props.onChange(checked);
    }
  }

  /**
   * handleClick - Click handler
   */
  handleClick(e) {
    e.preventDefault();

    if (!this.props.disabled) {
      const checkbox = this.input;
      this.setChecked(!checkbox.checked);
      if (this.props.focusOnClick) {
        checkbox.focus();
      }
      this.props.onClick(e);
    }
  }

  /**
   * handleInputFocus - handle input focus event
   */
  handleInputFocus() {
    this.setState({
      focused: true
    });
  }

  /**
   * handleInputBlur - handle input blur event
   */
  handleInputBlur() {
    this.setState({
      focused: false
    });
  }

  /**
   * handleInputChange - handle input change event
   */
  handleInputChange() {
    this.setChecked(this.input.checked);
  }

  /**
   * handleInputClick - handle input click event
   */
  handleInputClick(e) {
    // so that onClick handler of container div won't be called
    e.stopPropagation();
  }

  /**
   * baseRender
   */
  render(baseClass, renderInner) {
    const classes = classNames(
      baseClass,
      {
        [`${baseClass}--checked`]: this.state.checked,
        [`${baseClass}--focused`]: this.state.focused,
        [`${baseClass}--disabled`]: this.props.disabled
      },
      this.props.className
    );
    const moreProps = {};
    if (this.props.dataTestid) {
      moreProps['data-testid'] = this.props.dataTestid;
    }
    return (
      <div
        style={this.props.style}
        className={classes}
        onClick={e => this.handleClick(e)}
        {...moreProps}
        onTouchEnd={e => this.handleClick(e)}
      >
        {renderInner()}

        <input
          {...omit(this.props, 'defaultChecked', 'focusOnClick')}
          ref={ref => {
            this.input = ref;
          }}
          className="BaseOnOff__screenreader-only"
          type="checkbox"
          checked={this.state.checked}
          onFocus={() => this.handleInputFocus()}
          onBlur={() => this.handleInputBlur()}
          onChange={() => this.handleInputChange()}
          onClick={e => this.handleInputClick(e)}
        />
      </div>
    );
  }
}

BaseOnOff.defaultProps = {
  onClick: noop,
  focusOnClick: true
};

BaseOnOff.propTypes = {
  /**
   * independently controlling the checkbox without state.
   */
  checked: PropTypes.bool,
  /**
   * Whether the button is on by default, later will be controlled by the state.
   */
  defaultChecked: PropTypes.bool,
  /**
   * Whether the button is disabled
   */
  disabled: PropTypes.bool,
  /**
   * A callback function for an on change event
   */
  onChange: PropTypes.func,
  /**
   * A custom className for the component container
   */
  className: PropTypes.string,
  /**
   * A style object to apply on the component
   */
  style: PropTypes.object,
  /**
   * Callback to be called when clicked
   */
  onClick: PropTypes.func,
  /**
   * Boolean value indicating whether to call focus on the input on click
   */
  focusOnClick: PropTypes.bool,
  /**
   * data-testid for auto testing
   */
  dataTestid: PropTypes.string
};
